<script setup lang="ts">
// @ts-nocheck
import { ref, computed, watch } from 'vue'
import { usePluginSession } from './composables/usePluginSession'
import { usePostHog } from './composables/usePostHog'
import { useConvexQuery } from 'convex-vue'
import { api } from '~/convex/_generated/api'
import { useNotifications } from '@/composables/useNotifications'
import { Minus, CircleHelp, Loader2 } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import ChatTab from './tabs/ChatTab.vue'
import ChatGuideDialog from './ChatGuideDialog.vue'

const session = usePluginSession()
const posthog = usePostHog()
const { playMessageSound } = useNotifications()

const isExpanded = ref(false)
const showGuideDialog = ref(false)

const { data: messages } = useConvexQuery(
  api.chatMessages.getMessages,
  computed(() => {
    if (!session.chatRoomId.value || !session.userId.value) return undefined
    return { roomId: session.chatRoomId.value, userId: session.userId.value }
  })
)

const { data: lastReadTimestamp } = useConvexQuery(
  api.chatMessages.getLastReadTimestamp,
  computed(() => {
    if (!session.chatRoomId.value || !session.userId.value) return undefined
    return { roomId: session.chatRoomId.value, userId: session.userId.value }
  })
)

const unreadCount = computed(() => {
  if (!messages.value) return 0
  const sinceTimestamp = lastReadTimestamp.value ?? 0
  return messages.value.filter(
    m => m.timestamp > sinceTimestamp && m.senderId !== session.userId.value
  ).length
})

const { data: widgetRoomStatus } = useConvexQuery(
  api.chatRooms.getWidgetRoomStatus,
  computed(() => {
    if (!session.userId.value || !session.chatRoomId.value) return undefined
    return { roomId: session.chatRoomId.value, userId: session.userId.value }
  })
)

const isQueued = computed(() =>
  widgetRoomStatus.value?.status === 'open' && widgetRoomStatus.value?.queueStatus === 'queued'
)

const { data: queuePosition } = useConvexQuery(
  api.queue.getQueuePosition,
  computed(() => {
    if (!isQueued.value || !session.chatRoomId.value) return undefined
    return { roomId: session.chatRoomId.value }
  })
)

const headerSubtitle = computed(() => {
  if (!isQueued.value) return null
  if (!queuePosition.value) return 'Connecting to an agent…'
  const { position, activeAgents } = queuePosition.value
  // activeAgents counts agents with room under their chat limit, so 0 means
  // everyone is full or offline right now.
  if (activeAgents === 0) return `No agents available right now · #${position} in queue`
  const agentText = `${activeAgents} ${activeAgents === 1 ? 'agent' : 'agents'} available`
  return `#${position} in queue · ${agentText}`
})

watch(messages, (newMessages, oldMessages) => {
  if (!oldMessages || oldMessages.length === 0) return
  if (!newMessages || newMessages.length <= oldMessages.length) return

  const latestMessage = newMessages[newMessages.length - 1]
  if (latestMessage.senderId !== session.userId.value) {
    playMessageSound()
  }
}, { deep: true })

watch(showGuideDialog, (isOpen) => {
  if (isOpen) {
    posthog.trackHelpOpened({
      clientId: session.clientId.value,
      productId: session.productId.value
    })
  } else {
    posthog.trackHelpClosed({
      clientId: session.clientId.value,
      productId: session.productId.value
    })
  }
})

function handleTemplateSelect(template: string) {
  window.dispatchEvent(new CustomEvent('populate-chat-input', {
    detail: { template }
  }))

  posthog.trackTemplateUsed(template, {
    clientId: session.clientId.value,
    productId: session.productId.value
  })
}

function toggleWidget() {
  isExpanded.value = !isExpanded.value
  if (isExpanded.value) {
    posthog.trackWidgetOpened({
      clientId: session.clientId.value,
      productId: session.productId.value
    })
  }
}

function closeWidget() {
  isExpanded.value = false
  posthog.trackWidgetClosed({
    clientId: session.clientId.value,
    productId: session.productId.value
  })
}
</script>

<template>
  <div class="abba-widget-root">
    <Button
      v-if="!isExpanded"
      class="abba-widget-button abba-widget-pill"
      @click="toggleWidget"
    >
      <span class="abba-widget-pill-wave">👋</span>
      <span class="abba-widget-pill-label">Ask Hiyo</span>
      <span
        v-if="unreadCount > 0"
        class="abba-widget-badge"
      >{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
    </Button>

    <Card
      v-if="isExpanded"
      class="abba-widget-container"
    >
      <CardHeader class="abba-widget-header">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <span class="abba-widget-status-dot" />
            <div class="abba-widget-title-block">
              <div class="abba-widget-title-row">
                <span class="font-semibold">Support</span>
                <span
                  v-if="!isQueued"
                  class="abba-widget-title-meta"
                >usually replies in a minute</span>
                <span
                  v-else
                  class="abba-widget-title-meta flex items-center gap-1"
                >
                  <Loader2 class="w-3 h-3 animate-spin" />
                  {{ headerSubtitle }}
                </span>
              </div>
            </div>
          </div>

          <div class="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              class="abba-widget-header-action"
              title="How to use this"
              @click="showGuideDialog = true"
            >
              <CircleHelp class="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              class="abba-widget-header-action"
              title="Minimize"
              @click="closeWidget"
            >
              <Minus class="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent class="abba-widget-content">
        <div class="abba-widget-main">
          <div class="abba-widget-tab-content">
            <ChatTab />
          </div>
        </div>
      </CardContent>
    </Card>

    <ChatGuideDialog
      :open="showGuideDialog"
      @update:open="showGuideDialog = $event"
      @template-selected="handleTemplateSelect"
    />
  </div>
</template>

<style scoped>
</style>
